import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, ViewChild } from '@angular/core';

// Gap kept between the popup and the edge of whatever would clip it
const EDGE_GAP = 8;
const POPUP_MAX_WIDTH = 280;
// Space between the toggle and the popup (matches the SCSS offset)
const POPUP_OFFSET = 8;

interface ShareLinks {
  facebook: string;
  linkedin: string;
  whatsapp: string;
  email: string;
}

@Component({
  selector: 'app-program-share',
  templateUrl: './program-share.component.html',
  styleUrls: ['./program-share.component.scss']
})
export class ProgramShareComponent implements OnChanges {
  @Input() title?: string;
  @Input() url?: string;
  @Output() openChange = new EventEmitter<boolean>();

  isOpen = false;
  copied = false;
  shareUrl!: string;
  links!: ShareLinks;

  constructor(private host: ElementRef<HTMLElement>) {}

  // The popup is rendered by *ngIf, so fit it as soon as it exists
  @ViewChild('popup') set popupRef(ref: ElementRef<HTMLElement> | undefined) {
    if (ref) this.fitPopup(ref.nativeElement);
  }

  ngOnChanges(): void {
    this.shareUrl = this.url || window.location.href;
    const shareTitle = this.title || document.title;
    this.links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.shareUrl)}`,
      whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(shareTitle + ' ' + this.shareUrl)}`,
      email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(this.shareUrl)}`
    };
  }

  toggle(event: Event): void {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
    this.openChange.emit(this.isOpen);
  }

  copyLink(event: Event): void {
    event.stopPropagation();
    navigator.clipboard.writeText(this.shareUrl).then(() => {
      this.copied = true;
      setTimeout(() => (this.copied = false), 2000);
    });
  }

  /**
   * Keeps the popup fully visible: shrinks it to the available space, nudges
   * it sideways and opens it upwards when there is no room below, so the
   * nearest clipping ancestor (e.g. the mega-menu scroll box or the course
   * hero) or the viewport never cuts it off.
   */
  private fitPopup(popup: HTMLElement): void {
    const bounds = this.clipBounds();
    popup.style.width = `${Math.min(POPUP_MAX_WIDTH, bounds.right - bounds.left - EDGE_GAP * 2)}px`;
    popup.style.transform = '';
    popup.style.maxHeight = '';
    popup.classList.remove('program-share-popup--up');

    const rect = popup.getBoundingClientRect();
    let shift = 0;
    if (rect.left < bounds.left + EDGE_GAP) {
      shift = bounds.left + EDGE_GAP - rect.left;
    } else if (rect.right > bounds.right - EDGE_GAP) {
      shift = bounds.right - EDGE_GAP - rect.right;
    }
    popup.style.transform = shift ? `translateX(${shift}px)` : '';

    // Not enough room below: open on whichever side has more space
    if (rect.bottom > bounds.bottom - EDGE_GAP) {
      const toggle = this.host.nativeElement.getBoundingClientRect();
      const spaceBelow = bounds.bottom - toggle.bottom;
      const spaceAbove = toggle.top - bounds.top;
      const up = spaceAbove > spaceBelow;
      if (up) popup.classList.add('program-share-popup--up');
      // Still too tall for that side: scroll inside instead of being cut off
      const room = (up ? spaceAbove : spaceBelow) - POPUP_OFFSET - EDGE_GAP;
      if (rect.height > room) popup.style.maxHeight = `${Math.max(room, 0)}px`;
    }
  }

  /** Bounds of the nearest ancestor that clips overflow, limited to the viewport. */
  private clipBounds(): { left: number; right: number; top: number; bottom: number } {
    let left = 0;
    let top = 0;
    let right = document.documentElement.clientWidth;
    let bottom = document.documentElement.clientHeight;
    let el = this.host.nativeElement.parentElement;
    while (el && el !== document.body) {
      const style = getComputedStyle(el);
      if (style.overflowX !== 'visible' || style.overflowY !== 'visible') {
        const r = el.getBoundingClientRect();
        left = Math.max(left, r.left);
        right = Math.min(right, r.right);
        top = Math.max(top, r.top);
        bottom = Math.min(bottom, r.bottom);
        break;
      }
      el = el.parentElement;
    }
    return { left, right, top, bottom };
  }

  @HostListener('document:click')
  closeOnOutsideClick(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.openChange.emit(false);
    }
  }
}
